import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    OrderState,
    SmartPolicyAction,
    SmartPolicyBeforeFormattingWithFormattedEntities,
    SmartPolicyConfig,
    SmartPolicyCreationFlow,
    SmartPolicyEntityFullData,
    SmartPolicyWithEntitiesFullData
} from './SmartPoliciesTypes';
import { showAlert } from '@silverfort/sf-components';
import { httpDelete, httpGet, httpPost, httpPut } from '../../../../../components/utils';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { SMART_POLICY_TO_ADD } from './SmartPoliciesConsts';

type SmartPolicyContextType = {
    smartPoliciesConfig?: SmartPolicyConfig;
    loadingSmartPolicies: boolean;
    smartPolicies: SmartPolicyWithEntitiesFullData[];
    setOrderState: (state: OrderState) => void;
    snackAlert?: string;
    setSnackAlert: (alert?: string) => void;
    smartPolicyForEdit?: SmartPolicyWithEntitiesFullData;
    setSmartPolicyForEdit: (policy?: SmartPolicyWithEntitiesFullData) => void;
    createNewSmartPolicy: (params: {
        entities?: SmartPolicyEntityFullData[];
        creationFlow?: SmartPolicyCreationFlow;
    }) => void;
    isPolicyForEditValid: boolean;
    saveSmartPolicy?: () => Promise<void>;
    policyToDelete?: SmartPolicyWithEntitiesFullData;
    setPolicyToDelete: (policy?: SmartPolicyWithEntitiesFullData) => void;
    deletePolicy: () => Promise<void>;
};

const defaultState: SmartPolicyContextType = {
    smartPoliciesConfig: undefined,
    loadingSmartPolicies: false,
    smartPolicies: [],
    setOrderState: () => {},
    snackAlert: undefined,
    setSnackAlert: () => {},
    smartPolicyForEdit: undefined,
    setSmartPolicyForEdit: () => {},
    createNewSmartPolicy: () => {},
    isPolicyForEditValid: false,
    saveSmartPolicy: () => Promise.resolve(),
    policyToDelete: undefined,
    setPolicyToDelete: () => {},
    deletePolicy: () => Promise.resolve()
};

const SmartPolicyContext = createContext<SmartPolicyContextType>(defaultState);

export const SmartPolicyProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const [smartPoliciesConfig, setSmartPoliciesConfig] = useState<SmartPolicyConfig | undefined>();
    const [loadingSmartPolicies, setLoadingSmartPolicies] = useState<boolean>(false);
    const [smartPolicies, setSmartPolicies] = useState<SmartPolicyWithEntitiesFullData[]>([]);
    const [orderState, setOrderState] = useState<OrderState>({
        orderByField: 'name',
        orderDirection: 'asc'
    });
    const [snackAlert, setSnackAlert] = useState<string | undefined>(undefined);
    const [policyToDelete, setPolicyToDelete] = useState<SmartPolicyWithEntitiesFullData | undefined>(undefined);
    const [smartPolicyForEdit, setSmartPolicyForEdit] = useState<SmartPolicyWithEntitiesFullData | undefined>(
        undefined
    );
    const [creationFlow, setCreationFlow] = useState<SmartPolicyCreationFlow | undefined>(undefined);

    const isPolicyForEditValid: boolean = useMemo(() => {
        if (!smartPoliciesConfig || !smartPolicyForEdit) return false;

        const scopeTabValid = !!(
            smartPolicyForEdit.name && smartPolicyForEdit.entities.filter((entity) => !entity.excluded).length > 0
        );

        const isAnyActionIncluded =
            smartPolicyForEdit.activated_action.includes(SmartPolicyAction.ALTER_AFTER) ||
            smartPolicyForEdit.activated_action.includes(SmartPolicyAction.BLOCK_AFTER);
        const notifyActionValid =
            !smartPolicyForEdit.activated_action.includes(SmartPolicyAction.ALTER_AFTER) ||
            (smartPolicyForEdit.alter_after >= smartPoliciesConfig.minDaysToActivate &&
                smartPolicyForEdit.alter_after <= smartPoliciesConfig.maxDaysToActivate);
        const denyActionValid =
            !smartPolicyForEdit.activated_action.includes(SmartPolicyAction.BLOCK_AFTER) ||
            (smartPolicyForEdit.block_after >= smartPoliciesConfig.minDaysToActivate &&
                smartPolicyForEdit.block_after <= smartPoliciesConfig.maxDaysToActivate);
        const actionsTabValid = notifyActionValid && denyActionValid;

        const limitActivitiesValid =
            !smartPolicyForEdit.is_limit_activities ||
            (smartPolicyForEdit.days_limit_activities >= smartPoliciesConfig.minDaysToLimitActivities &&
                smartPolicyForEdit.days_limit_activities <= smartPoliciesConfig.maxDaysToLimitActivities);

        return isAnyActionIncluded && scopeTabValid && actionsTabValid && limitActivitiesValid;
    }, [smartPolicyForEdit, smartPoliciesConfig]);

    const getSmartPoliciesConfig = useCallback(async () => {
        try {
            const response = await httpGet('/serviceAccountsV2/smartPolicies/config');
            const data = response.data as SmartPolicyConfig | null;
            setSmartPoliciesConfig(data || undefined);
        } catch (error) {
            console.error('Error fetching smart policies config:', error);
        }
    }, []);

    useEffect(() => {
        getSmartPoliciesConfig();
    }, [getSmartPoliciesConfig]);

    const getSmartPolicies = useCallback(async () => {
        try {
            setLoadingSmartPolicies(true);
            const response = await httpGet('/serviceAccountsV2/smartPolicies', {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: { ...orderState }
                }
            });
            const data = response.data as { smartPolicies: SmartPolicyBeforeFormattingWithFormattedEntities[] } | null;
            const formattedData: SmartPolicyWithEntitiesFullData[] =
                data?.smartPolicies.map((policy) => ({
                    ...policy,
                    update_time: new Date(policy.update_time),
                    created_time: new Date(policy.created_time),
                    last_run: policy.last_run ? new Date(policy.last_run) : undefined
                })) || [];
            setSmartPolicies(formattedData);
        } catch (error) {
            console.error('Error fetching smart policies:', error);
        } finally {
            setLoadingSmartPolicies(false);
        }
    }, [orderState]);

    useEffect(() => {
        getSmartPolicies();
    }, [getSmartPolicies]);

    const createNewSmartPolicy = useCallback(
        ({
            entities,
            creationFlow
        }: {
            entities?: SmartPolicyEntityFullData[];
            creationFlow?: SmartPolicyCreationFlow;
        }) => {
            const newSmartPolicy: SmartPolicyWithEntitiesFullData = JSON.parse(JSON.stringify(SMART_POLICY_TO_ADD));
            newSmartPolicy.alter_after = smartPoliciesConfig?.defaultDayActivateAlter || 30;
            newSmartPolicy.block_after = smartPoliciesConfig?.defaultDayActivateBlock || 90;
            newSmartPolicy.days_limit_activities = smartPoliciesConfig?.defaultDaysLimitActivities || 90;
            newSmartPolicy.entities = entities ? entities : [];
            setSmartPolicyForEdit(newSmartPolicy);
            setCreationFlow(creationFlow);
        },
        [smartPoliciesConfig]
    );

    const saveSmartPolicy = useCallback(async () => {
        if (!smartPolicyForEdit || !isPolicyForEditValid) return;

        if (
            smartPolicyForEdit.activated_action.includes(SmartPolicyAction.BLOCK_AFTER) &&
            smartPolicyForEdit.activated_action.includes(SmartPolicyAction.ALTER_AFTER) &&
            smartPolicyForEdit.block_after <= smartPolicyForEdit.alter_after
        ) {
            showAlert(
                'To ensure adequate monitoring, the Deny policy must have a longer activation period than the Notify policy.',
                'error'
            );
            return;
        }

        try {
            const { id, ...smartPolicyWithoutId } = smartPolicyForEdit;
            const smartPolicyWithEntities = {
                ...smartPolicyWithoutId,
                entities: smartPolicyForEdit.entities.map((entity) => ({
                    guid: entity.guid,
                    type: entity.type,
                    excluded: entity.excluded
                }))
            };

            if (id === -1) {
                // Create new smart policy
                await httpPost('/serviceAccountsV2/smartPolicies/', {
                    smartPolicy: smartPolicyWithEntities,
                    creationFlow
                });
                setSnackAlert(`"${smartPolicyWithEntities.name}" added successfully`);
            } else {
                // Update existing smart policy
                await httpPut(`/serviceAccountsV2/smartPolicies/${id}`, smartPolicyWithEntities);
                setSnackAlert(`Save changes successfully to "${smartPolicyWithEntities.name}"`);
            }
            setSmartPolicyForEdit(undefined);
            setCreationFlow(undefined);
            await getSmartPolicies();
            console.log('Saving smart policy:', smartPolicyForEdit);
        } catch (error) {
            showAlert('Operation failed or partially completed.', 'error');
            console.error('Error saving smart policy:', error);
        }
    }, [smartPolicyForEdit, isPolicyForEditValid, getSmartPolicies, creationFlow]);

    const deletePolicy = useCallback(async () => {
        if (!policyToDelete) return;

        try {
            await httpDelete(`/serviceAccountsV2/smartPolicies/${policyToDelete.id}`);
            await getSmartPolicies();
            setPolicyToDelete(undefined);
        } catch (error) {
            showAlert('Operation failed or partially completed.', 'error');
            console.error('Error deleting smart policy:', error);
        }
    }, [getSmartPolicies, policyToDelete]);

    return (
        <SmartPolicyContext.Provider
            value={{
                smartPoliciesConfig,
                loadingSmartPolicies,
                smartPolicies,
                setOrderState,
                snackAlert,
                setSnackAlert,
                smartPolicyForEdit,
                setSmartPolicyForEdit,
                createNewSmartPolicy,
                isPolicyForEditValid,
                saveSmartPolicy,
                policyToDelete,
                setPolicyToDelete,
                deletePolicy
            }}>
            {children}
        </SmartPolicyContext.Provider>
    );
};

export const useSmartPolicyContext = () => {
    const context = useContext(SmartPolicyContext);
    if (!context) {
        throw new Error('useSmartPolicy must be used within SmartPolicyProvider');
    }
    return context;
};
